import { useCallback, useRef, useState } from "react";
import { useSnackBar } from "../stores";

// Generic constraint for UseApiFetchOptions to carry the result type.
export type UseApiFetchOptions<T = unknown> = {
  successMsg?: (result: T) => string | undefined | false;
  showErrorAlert?: boolean;
  resetDataOnError?: boolean;
};

export type UseApiCallback<TData, TResult> = (
  data: TData | undefined,
) => Promise<TResult>;

export type UseApiFetch<TData> = <TResult = TData>(
  cb?: UseApiCallback<TData, TResult>,
  options?: UseApiFetchOptions<TResult>,
) => Promise<TResult>;

export default function useApi<TData = unknown>() {
  const [data, setData] = useState<TData | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackBar();
  const dataRef = useRef<TData | undefined>(undefined);
  dataRef.current = data;

  const fetch: UseApiFetch<TData> = useCallback(
    async <TResult = TData>(
      cb?: UseApiCallback<TData, TResult>,
      {
        showErrorAlert = true,
        successMsg,
        resetDataOnError = false,
      }: UseApiFetchOptions<TResult> = {},
    ) => {
      setLoading(true);
      try {
        const callback: UseApiCallback<TData, TResult> =
          cb ??
          (async () => undefined as unknown as TResult);

        const result = await callback(dataRef.current);
        setData(result as unknown as TData);
        setError(undefined);
        const successMessage = successMsg?.(result);
        if (!!successMessage) {
          showSnackbar(successMessage);
        }
        return result;
      } catch (err: any) {
        setError(err);
        if (resetDataOnError) setData(undefined);
        if (showErrorAlert) {
          showSnackbar(err.message, { type: "error" });
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar],
  );

  return { fetch, data, error, loading };
}
